import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { portalApi } from './portalApi';

export const REPORTS_CACHE_KEY = 'portal_reports_cache';
const TTL_MS = 6 * 60 * 60 * 1000;
const MAX_ENTRIES = 5;
const CONCURRENCY = 4;
export const SECTION_KEYS = [
  'usability-overview', 'top-pages', 'device-breakdown', 'browser-breakdown',
  'source-medium-breakdown', 'demographics', 'purchase-funnel', 'checkout-funnel',
];
const FRIENDLY_ERROR = 'Não foi possível carregar esta métrica agora';

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Day 1 → yesterday; on day 1, the full previous month (same rule as the backend snapshot).
export const currentMonthRange = () => {
  const t = new Date();
  const today = new Date(t.getFullYear(), t.getMonth(), t.getDate());
  const end = new Date(today); end.setDate(end.getDate() - 1);
  const start = today.getDate() === 1 ? new Date(end.getFullYear(), end.getMonth(), 1) : new Date(today.getFullYear(), today.getMonth(), 1);
  return { startDate: ymd(start), endDate: ymd(end) };
};

export const rangeKey = (s, e) => `${s}|${e}`;

const isFresh = (entry) => entry && Date.now() - new Date(entry.updatedAt).getTime() < TTL_MS;
const isSettled = (sec) => sec && (sec.status === 'done' || sec.status === 'error');

const readStorage = (projectId) => {
  try {
    const raw = JSON.parse(sessionStorage.getItem(REPORTS_CACHE_KEY) || 'null');
    if (!raw || raw.projectId !== projectId) return { entries: {}, order: [] };
    const entries = {};
    (raw.order || []).forEach((k) => {
      const e = raw.entries?.[k];
      if (!isFresh(e)) return;
      const sections = {};
      Object.entries(e.sections || {}).forEach(([s, v]) => { if (isSettled(v)) sections[s] = v; });
      entries[k] = { ...e, sections, loading: false };
    });
    return { entries, order: (raw.order || []).filter((k) => entries[k]), periodFilter: raw.periodFilter || null };
  } catch (e) {
    return { entries: {}, order: [] };
  }
};

const PortalReportsContext = createContext(null);
export const usePortalReports = () => useContext(PortalReportsContext);

export const PortalReportsProvider = ({ projectId, autoPrefetch, children }) => {
  const initial = useMemo(() => readStorage(projectId), [projectId]);
  const [entries, setEntries] = useState(initial.entries);
  const [order, setOrder] = useState(initial.order);
  const [estimates, setEstimates] = useState({});
  const [currentKey, setCurrentKey] = useState(null);
  const [readyUnseen, setReadyUnseen] = useState(false);
  const [periodFilter, setPeriodFilter] = useState(initial.periodFilter || null);
  const prefetched = useRef(false);
  const metricsVisible = useRef(false);
  const queue = useRef([]);
  const running = useRef(0);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  useEffect(() => {
    try {
      const persist = {};
      order.forEach((k) => {
        const e = entries[k];
        if (!e) return;
        const sections = {};
        Object.entries(e.sections).forEach(([s, v]) => { if (isSettled(v)) sections[s] = v; });
        persist[k] = { ...e, sections };
      });
      sessionStorage.setItem(REPORTS_CACHE_KEY, JSON.stringify({ projectId, entries: persist, order, periodFilter }));
    } catch (e) { /* storage full or unavailable */ }
  }, [entries, order, projectId, periodFilter]);

  const loadEstimates = useCallback(() => {
    portalApi.get('/api/portal/relatorios/estimativa').then((r) => setEstimates(r.data?.sections || {})).catch(() => {});
  }, []);

  const touch = useCallback((key) => {
    setOrder((prev) => {
      const next = [key, ...prev.filter((k) => k !== key)];
      const dropped = next.slice(MAX_ENTRIES);
      if (dropped.length) setEntries((e) => { const c = { ...e }; dropped.forEach((k) => delete c[k]); return c; });
      return next.slice(0, MAX_ENTRIES);
    });
  }, []);

  const setSection = useCallback((key, section, patch) => {
    setEntries((prev) => {
      const e = prev[key];
      if (!e) return prev;
      const sections = { ...e.sections, [section]: { ...e.sections[section], ...patch } };
      const allSettled = SECTION_KEYS.every((s) => isSettled(sections[s]));
      const next = { ...e, sections, ...(allSettled && e.loading ? { loading: false, updatedAt: new Date().toISOString(), loadedAt: new Date().toISOString(), source: 'live', finishedAt: Date.now() } : {}) };
      if (allSettled && e.loading) {
        if (!metricsVisible.current) setReadyUnseen(true);
        setTimeout(loadEstimates, 0);
      }
      return { ...prev, [key]: next };
    });
  }, [loadEstimates]);

  const pump = useCallback(() => {
    while (running.current < CONCURRENCY && queue.current.length) {
      const task = queue.current.shift();
      const e = entriesRef.current[task.key];
      if (!e) continue;
      running.current += 1;
      setSection(task.key, task.section, { status: 'loading', startedAt: Date.now() });
      const t0 = Date.now();
      portalApi.get(`/api/portal/relatorios/${task.section}`, { params: { start_date: e.startDate, end_date: e.endDate } })
        .then((r) => {
          const ok = r.data?.success;
          setSection(task.key, task.section, ok
            ? { status: 'done', payload: r.data, error: null, ms: Date.now() - t0 }
            : { status: 'error', error: FRIENDLY_ERROR, ms: Date.now() - t0 });
        })
        .catch(() => setSection(task.key, task.section, { status: 'error', error: FRIENDLY_ERROR, ms: Date.now() - t0 }))
        .finally(() => { running.current -= 1; pump(); });
    }
  }, [setSection]);

  const enqueue = useCallback((key, sections) => {
    queue.current = [...sections.map((section) => ({ key, section })), ...queue.current.filter((t) => !(t.key === key && sections.includes(t.section)))];
    pump();
  }, [pump]);

  // Loads (or reloads) the given range. keepData keeps current payloads visible until replaced.
  const load = useCallback((startDate, endDate, { sections = SECTION_KEYS, keepData = false } = {}) => {
    const key = rangeKey(startDate, endDate);
    const old = entriesRef.current[key];
    const keepOld = old && (keepData || sections !== SECTION_KEYS);
    const secs = keepOld ? { ...old.sections } : {};
    sections.forEach((s) => {
      secs[s] = { status: 'queued', startedAt: null, payload: keepData ? old?.sections[s]?.payload ?? null : null };
    });
    const entry = { startDate, endDate, sections: secs, loading: true, startedAt: Date.now(), updatedAt: old?.updatedAt || new Date().toISOString(), loadedAt: old?.loadedAt || null, source: old?.source || 'live', generatedAt: old?.generatedAt || null };
    entriesRef.current = { ...entriesRef.current, [key]: entry };
    setEntries((prev) => ({ ...prev, [key]: entry }));
    touch(key);
    setReadyUnseen(false);
    enqueue(key, sections);
    return key;
  }, [enqueue, touch]);

  // Shows a range: instant from cache when fresh, else loads what is missing.
  const ensure = useCallback((startDate, endDate) => {
    const key = rangeKey(startDate, endDate);
    setCurrentKey(key);
    const e = entriesRef.current[key];
    if (e?.loading) {
      touch(key);
      return key;
    }
    if (e && isFresh(e)) {
      touch(key);
      const missing = SECTION_KEYS.filter((s) => !e.sections[s]);
      if (missing.length && !e.loading) load(startDate, endDate, { sections: missing });
      return key;
    }
    return load(startDate, endDate, { keepData: !!e });
  }, [load, touch]);

  const refresh = useCallback((startDate, endDate) => {
    setCurrentKey(rangeKey(startDate, endDate));
    load(startDate, endDate, { keepData: true });
  }, [load]);

  const retry = useCallback((key, section) => {
    const e = entriesRef.current[key];
    if (!e) return;
    setEntries((prev) => ({ ...prev, [key]: { ...prev[key], loading: true } }));
    setSection(key, section, { status: 'queued', error: null });
    enqueue(key, [section]);
  }, [enqueue, setSection]);

  const setMetricsVisible = useCallback((v) => {
    metricsVisible.current = v;
    if (v) setReadyUnseen(false);
  }, []);

  // Background prefetch of the current month (snapshot first, then live if missing/stale).
  useEffect(() => {
    loadEstimates();
    if (!autoPrefetch || prefetched.current) return;
    prefetched.current = true;
    const { startDate, endDate } = currentMonthRange();
    const key = rangeKey(startDate, endDate);
    setCurrentKey((k) => k || key);
    const cached = entriesRef.current[key];
    if (cached && isFresh(cached) && SECTION_KEYS.every((s) => cached.sections[s])) return;
    portalApi.get('/api/portal/relatorios/snapshot', { params: { periodo: 'mes_atual' } })
      .then((r) => {
        const snap = r.data;
        const skey = rangeKey(snap.data_inicio, snap.data_fim);
        const sections = {};
        SECTION_KEYS.forEach((s) => {
          const p = snap.sections?.[s];
          sections[s] = p?.success ? { status: 'done', payload: p } : { status: 'error', error: FRIENDLY_ERROR };
        });
        const entry = { startDate: snap.data_inicio, endDate: snap.data_fim, sections, loading: false, updatedAt: snap.generated_at, loadedAt: snap.generated_at, generatedAt: snap.generated_at, source: 'snapshot' };
        entriesRef.current = { ...entriesRef.current, [skey]: entry };
        setEntries((prev) => ({ ...prev, [skey]: entry }));
        touch(skey);
        setCurrentKey((k) => (k === key ? skey : k || skey));
        if (!isFresh(entry)) load(snap.data_inicio, snap.data_fim, { keepData: true });
      })
      .catch(() => { if (!entriesRef.current[key]?.loading) load(startDate, endDate); });
  }, []);

  const value = useMemo(() => ({
    entries, estimates, currentKey, readyUnseen, ensure, refresh, retry, setMetricsVisible, periodFilter, setPeriodFilter,
  }), [entries, estimates, currentKey, readyUnseen, ensure, refresh, retry, setMetricsVisible, periodFilter]);

  return <PortalReportsContext.Provider value={value}>{children}</PortalReportsContext.Provider>;
};

// Progress of one entry: done count, total and estimated remaining ms (null = unknown).
export const computeProgress = (entry, estimates, now) => {
  if (!entry) return null;
  const secs = SECTION_KEYS.map((s) => [s, entry.sections[s]]);
  const done = secs.filter(([, v]) => isSettled(v)).length;
  const finishedMs = secs.filter(([, v]) => v?.status === 'done' && v.ms).map(([, v]) => v.ms);
  const localAvg = finishedMs.length ? finishedMs.reduce((a, b) => a + b, 0) / finishedMs.length : null;
  let remaining = 0;
  let known = true;
  secs.forEach(([s, v]) => {
    if (isSettled(v)) return;
    const avg = estimates[s] ?? localAvg;
    if (avg == null) { known = false; return; }
    remaining += v?.status === 'loading' && v.startedAt ? Math.max(avg - (now - v.startedAt), 0) : avg;
  });
  return { done, total: SECTION_KEYS.length, pct: Math.round((done / SECTION_KEYS.length) * 100), remainingMs: known ? remaining : null };
};
